import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { AgGridReact } from "ag-grid-react";
import React, { useCallback, useMemo, useState } from "react";
import { Button, Modal, Card, Header } from "semantic-ui-react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import CompanyPage from "../company/CompanyPage";
import "../css/home.css";
import Utils from "../util/Utils";
import {
  PERF_METRICS,
  buildPerfChartData,
  HorizontalPerfBarChart,
} from "./PerformanceBarChartsShared";
import "./styles.css";

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`etf-screener-tabpanel-${index}`}
      aria-labelledby={`etf-screener-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 1 }}>{children}</Box>}
    </div>
  );
}

export default function ETFScreener(props) {
  const [stateValues, setstateValues] = useState({
    open: false,
    modalSymbol: "",
  });
  const [tabIndex, setTabIndex] = useState(0);

  const showModal = (event, symbol) => {
    const col_heading = event.colDef.field;
    if (col_heading === "symbol") {
      setstateValues({ ...stateValues, modalSymbol: symbol, open: true });
    }
  };

  const setOpen = (flag) => {
    setstateValues({ ...stateValues, open: flag });
  };

  const openSymbolProfile = useCallback((symbol) => {
    if (!symbol) return;
    setstateValues((prev) => ({ ...prev, modalSymbol: symbol, open: true }));
  }, []);

  const defaultColDef = { resizable: true, floatingFilter: true };

  const overlayLoadingTemplate =
    '<span class="ag-overlay-loading-center">Please wait while data loading</span>';

  const ColourCellRenderer = (cellProps) => (
    <span
      style={{
        color: "blue",
        cursor: "pointer",
        textDecoration: "underline",
      }}
    >
      {cellProps.value}
    </span>
  );

  const columnDefs = [
    {
      headerName: "ETF",
      field: "symbol",
      filter: "agTextColumnFilter",
      sortable: true,
      width: 70,
      pinned: "left",
      cellRenderer: ColourCellRenderer,
    },
    {
      headerName: "ETF Name",
      field: "companyName",
      filter: "agTextColumnFilter",
      sortable: true,
      width: 350,
      pinned: "left",
    },
    {
      headerName: "Exchange",
      headerTooltip: "exchange",
      field: "exchangeShortName",
      filter: "agTextColumnFilter",
      sortable: true,
      width: 80,
      pinned: "left",
    },
    {
      headerName: "Market Cap",
      headerTooltip: "Market Cap",
      field: "marketCap",
      filter: "agNumberColumnFilter",
      sortable: true,
      width: 80,
      valueFormatter: Utils.ag_currencyCompactFormatter,
      pinned: "left",
    },
    {
      headerName: "Dividend",
      headerTooltip: "Dividend",
      field: "lastDiv",
      filter: "agNumberColumnFilter",
      valueFormatter: Utils.ag_currencyFormatter,
      sortable: true,
      width: 110,
      pinned: "left",
    },
    {
      headerName: "Current Price",
      headerTooltip: "Current Price",
      field: "price",
      filter: "agNumberColumnFilter",
      valueFormatter: Utils.ag_currencyFormatter,
      sortable: true,
      width: 120,
      pinned: "left",
    },
    {
      headerName: "Perf 1 day",
      headerTooltip: "Perf to Day",
      field: "perfDay",
      filter: "agNumberColumnFilter",
      sortable: true,
      width: 100,
      valueFormatter: Utils.ag_RatioFormatter,
      cellStyle: Utils.ag_grid_cell_color_neg_val,
    },
    {
      headerName: "Perf to Week",
      headerTooltip: "Perf to Week",
      field: "perfWeek",
      filter: "agNumberColumnFilter",
      sortable: true,
      width: 120,
      valueFormatter: Utils.ag_RatioFormatter,
      cellStyle: Utils.ag_grid_cell_color_neg_val,
    },
    {
      headerName: "Perf to Month",
      headerTooltip: "Perf to Month",
      field: "perfMonth",
      filter: "agNumberColumnFilter",
      sortable: true,
      width: 120,
      valueFormatter: Utils.ag_RatioFormatter,
      cellStyle: Utils.ag_grid_cell_color_neg_val,
    },
    {
      headerName: "Perf to YTD",
      headerTooltip: "Perf to YTD",
      field: "perfYTD",
      filter: "agNumberColumnFilter",
      sortable: true,
      width: 120,
      valueFormatter: Utils.ag_RatioFormatter,
      cellStyle: Utils.ag_grid_cell_color_neg_val,
    },
    {
      headerName: "Perf to Year",
      headerTooltip: "Perf to Year",
      field: "perfYear",
      filter: "agNumberColumnFilter",
      sortable: true,
      width: 120,
      valueFormatter: Utils.ag_RatioFormatter,
      cellStyle: Utils.ag_grid_cell_color_neg_val,
    },
    {
      headerName: "5Y (Annualized)",
      headerTooltip: "Compound annual growth rate over 5 years",
      field: "perf5Year",
      filter: "agNumberColumnFilter",
      sortable: true,
      width: 130,
      valueFormatter: Utils.ag_RatioFormatter,
      cellStyle: Utils.ag_grid_cell_color_neg_val,
    },
    {
      headerName: "5Y (Actual return)",
      headerTooltip: "Total return over 5 years (not annualized)",
      field: "perf5YearTotal",
      filter: "agNumberColumnFilter",
      sortable: true,
      width: 140,
      valueFormatter: Utils.ag_RatioFormatter,
      cellStyle: Utils.ag_grid_cell_color_neg_val,
    },
    {
      headerName: "10Y (Annualized)",
      headerTooltip: "Compound annual growth rate over 10 years",
      field: "perf10Year",
      filter: "agNumberColumnFilter",
      sortable: true,
      width: 135,
      valueFormatter: Utils.ag_RatioFormatter,
      cellStyle: Utils.ag_grid_cell_color_neg_val,
    },
    {
      headerName: "10Y (Actual return)",
      headerTooltip: "Total return over 10 years (not annualized)",
      field: "perf10YearTotal",
      filter: "agNumberColumnFilter",
      sortable: true,
      width: 145,
      valueFormatter: Utils.ag_RatioFormatter,
      cellStyle: Utils.ag_grid_cell_color_neg_val,
    },
  ];

  const stockReports = props.portfolio?.stockReports;

  const etfChartsByMetric = useMemo(() => {
    const out = {};
    for (const m of PERF_METRICS) {
      out[m.key] = buildPerfChartData(stockReports, m.key);
    }
    return out;
  }, [stockReports]);

  return (
    <>
      <div
        style={{
          marginTop: "0.5%",
        }}
      >
        <Card fluid raised>
          <Card.Content>
            <Card.Description>
              <Header color="blue" textAlign="center" as="h3">
                Index and Sector ETFs Performance
              </Header>
            </Card.Description>
          </Card.Content>
        </Card>
        <Box sx={{ borderBottom: 1, borderColor: "divider", mt: 1 }}>
          <Tabs
            value={tabIndex}
            onChange={(_, v) => setTabIndex(v)}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="Index and sector tracker views"
          >
            <Tab label="Table" id="etf-screener-tab-0" aria-controls="etf-screener-tabpanel-0" />
            <Tab
              label="Performance charts"
              id="etf-screener-tab-1"
              aria-controls="etf-screener-tabpanel-1"
            />
          </Tabs>
        </Box>
      </div>

      <TabPanel value={tabIndex} index={0}>
        <div
          className="ag-theme-quartz"
          style={{
            margin: "5px",
            height: "80vh",
            justifyContent: "center",
          }}
        >
          <AgGridReact
            defaultColDef={defaultColDef}
            columnDefs={columnDefs}
            rowData={props.portfolio ? props.portfolio.stockReports : ""}
            pagination={true}
            autoSizePadding={true}
            compact={true}
            overlayLoadingTemplate={overlayLoadingTemplate}
            onCellClicked={showModal}
          />
        </div>
      </TabPanel>

      <TabPanel value={tabIndex} index={1}>
        <Box
          sx={{
            px: { xs: 1, sm: 2 },
            pb: 4,
            maxWidth: { xs: "100%", md: 1320 },
            mx: "auto",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ mt: 0.5, mb: 0.5, fontWeight: 700, width: "100%", textAlign: "center" }}
          >
            ETF and index performance
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 1.5, display: "block", width: "100%", textAlign: "center" }}
          >
          </Typography>
          <Grid container spacing={2} sx={{ width: "100%", alignItems: "stretch" }}>
            {PERF_METRICS.map((m) => {
              const data = etfChartsByMetric[m.key] || [];
              return (
                <Grid item xs={12} sm={6} key={m.key}>
                  <Box
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "stretch",
                      pb: 1.5,
                      borderBottom: 1,
                      borderColor: "divider",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 700,
                        mb: 0.5,
                        color: "text.primary",
                        letterSpacing: 0.02,
                        width: "100%",
                        textAlign: "center",
                      }}
                    >
                      {m.label.toUpperCase()}
                    </Typography>
                    {!data.length ? (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mb: 1, display: "block", textAlign: "center", flex: 1 }}
                      >
                        No data for this period.
                      </Typography>
                    ) : (
                      <HorizontalPerfBarChart
                        data={data}
                        metricLabel={m.label}
                        chartKey={`etf-${m.key}`}
                        onSymbolClick={openSymbolProfile}
                        fillContainer
                      />
                    )}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </TabPanel>

      <Modal
        closeOnEscape={true}
        onClose={() => setOpen(false)}
        centered={true}
        open={stateValues.open}
        size="fullscreen"
        style={{ marginLeft: "2%" }}
      >
        <Modal.Content scrolling>
          <Modal.Description>
            <CompanyPage symbol={stateValues.modalSymbol} />
          </Modal.Description>
        </Modal.Content>
        <Modal.Actions>
          <Button className="ui blue button" onClick={() => setOpen(false)}>
            Close
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
}
